import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip, 
  Avatar, 
  CircularProgress, 
  useTheme, 
  Alert, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  PeopleAlt as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as CheckInIcon,
  Logout as CheckOutIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../utils/api';

// Types
interface DashboardStats {
  totalVisitors: number;
  todayVisitors: number;
  checkedIn: number;
  preRegistered: number;
}

interface Visitor {
  id: number;
  name: string;
  company: string;
  purpose: string;
  host_name: string;
  check_in_time: string | null;
  check_out_time: string | null;
  status: 'pending' | 'checked-in' | 'checked-out';
  photo_url: string | null;
}

// Status chip component
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  let color: 'success' | 'error' | 'warning' | 'default' = 'default';
  let label = status;
  
  switch (status) {
    case 'checked-in':
      color = 'success';
      label = 'Checked In';
      break;
    case 'checked-out':
      color = 'error';
      label = 'Checked Out';
      break;
    case 'pending':
      color = 'warning';
      label = 'Pending';
      break;
  }
  
  return (
    <Chip 
      label={label} 
      color={color} 
      size="small" 
      sx={{ 
        fontWeight: 500,
        borderRadius: '6px',
        textTransform: 'capitalize'
      }} 
    />
  );
};

// Stat card component
const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}> = ({ title, value, icon, color, onClick }) => {
  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: onClick ? 'translateY(-5px)' : 'none',
          boxShadow: onClick ? '0 8px 16px rgba(0,0,0,0.1)' : 'inherit',
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48, mr: 2 }}>
            {icon}
          </Avatar>
          <Typography variant="h5" component="div" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentVisitors, setRecentVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const theme = useTheme();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch statistics
        const statsResponse = await api.get('/api/dashboard/stats');
        setStats(statsResponse.data);
        
        // Fetch recent visitors
        const visitorsResponse = await api.get('/api/dashboard/recent-visitors');
        setRecentVisitors(visitorsResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        // Set default values for stats when API fails
        setStats({
          totalVisitors: 0,
          todayVisitors: 0,
          checkedIn: 0,
          preRegistered: 0
        });
        setRecentVisitors([]);
        setError('Failed to load dashboard statistics');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome to your visitor management dashboard. Here's an overview of today's activity.
        </Typography>
      </Box>
      
      {/* Quick action buttons */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<CheckInIcon />}
            onClick={() => navigate('/check-in')}
            sx={{ py: 1.5 }}
          >
            Check In Visitor
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<CheckOutIcon />}
            onClick={() => navigate('/visitors')}
            sx={{ py: 1.5 }}
          >
            Check Out Visitor
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/pre-register')}
            sx={{ py: 1.5 }}
          >
            Pre-Register Visitor
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<PeopleIcon />}
            onClick={() => navigate('/visitors')}
            sx={{ py: 1.5 }}
          >
            View All Visitors
          </Button>
        </Grid>
      </Grid>
      
      {/* Stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Visitors"
            value={stats?.totalVisitors || 0}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
            onClick={() => navigate('/visitors')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Visitors"
            value={stats?.todayVisitors || 0}
            icon={<TrendingUpIcon />}
            color={theme.palette.info.main}
            onClick={() => navigate('/visitors')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Currently Checked In"
            value={stats?.checkedIn || 0}
            icon={<CheckInIcon />}
            color={theme.palette.success.main}
            onClick={() => navigate('/visitors?status=checked-in')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pre-Registered"
            value={stats?.preRegistered || 0}
            icon={<PersonAddIcon />}
            color={theme.palette.warning.main}
            onClick={() => navigate('/visitors?status=pending')}
          />
        </Grid>
      </Grid>
      
      {/* Recent visitors */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Recent Visitors
          </Typography>
          <Button 
            variant="text" 
            color="primary"
            onClick={() => navigate('/visitors')}
          >
            View All
          </Button>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Visitor</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Check In/Out</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentVisitors.length > 0 ? (
                recentVisitors.map((visitor) => (
                  <TableRow 
                    key={visitor.id}
                    hover
                    onClick={() => navigate(`/visitors/${visitor.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={visitor.photo_url || undefined} 
                          sx={{ mr: 2, bgcolor: theme.palette.primary.main }}
                        >
                          {visitor.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {visitor.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {visitor.company}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{visitor.purpose}</TableCell>
                    <TableCell>{visitor.host_name}</TableCell>
                    <TableCell>
                      {visitor.check_in_time && (
                        <Typography variant="body2">
                          In: {format(new Date(visitor.check_in_time), 'h:mm a')}
                        </Typography>
                      )}
                      {visitor.check_out_time && (
                        <Typography variant="body2">
                          Out: {format(new Date(visitor.check_out_time), 'h:mm a')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={visitor.status} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No recent visitors
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Dashboard;
